
/**
 * Utilities for managing cache and browser refreshes
 * This module is now a lightweight wrapper around versionManager.tsx
 */

// App version from environment or build time
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'default_version';

// Flag to prevent infinite refresh loops
let refreshAttempted = false;

/**
 * Detects if the application has been updated since the last visit
 * @returns boolean - true if a new version is detected
 */
export const detectNewVersion = (): boolean => {
  try {
    const storedVersion = localStorage.getItem('app_version');
    const currentVersion = APP_VERSION;
    
    // Skip version check if using a timestamp-based version (prevents refresh loops)
    // Also skip on same-session visibility changes to prevent tab-switch refreshes
    if (document.visibilityState === 'visible' && document.hidden === false) {
      console.log('Skipping version check on tab visibility change');
      return false;
    }
    
    // Skip version check if it's a dynamic timestamp-based version
    if (currentVersion.includes('_') && currentVersion.includes(new Date().getFullYear().toString())) {
      console.log('Using dynamic timestamp-based version, skipping version check');
      return false;
    }
    
    // Only check for significant version differences, not minor variations
    const isNewVersion = storedVersion !== currentVersion && 
                        storedVersion !== null && 
                        !currentVersion.startsWith(storedVersion.split('.')[0]);
    
    if (isNewVersion) {
      console.log('Major application version update detected:', currentVersion, 'previous:', storedVersion);
      localStorage.setItem('app_version', currentVersion);
      return true;
    }
    
    // First visit, just set the version without triggering refresh
    if (storedVersion === null) {
      localStorage.setItem('app_version', currentVersion);
    }
    
    return false;
  } catch (error) {
    // In case localStorage is not available (private browsing, etc.)
    console.error('Error checking for new version:', error);
    return false;
  }
};

/**
 * Adds cache-busting query parameter to a URL
 * @param url - The URL to append the cache-busting parameter to
 * @returns The URL with cache-busting parameter
 */
export const addCacheBuster = (url: string): string => {
  const separator = url.includes('?') ? '&' : '?';
  // Use stable APP_VERSION instead of timestamp to prevent constant differences
  return `${url}${separator}v=${APP_VERSION}`;
};

/**
 * Create a cache-busting key for React component keys
 * @param baseKey - Base key to use
 * @returns A key including the version
 */
export const createCacheKey = (baseKey: string): string => {
  return `${baseKey}-${APP_VERSION}`;
};

/**
 * Detect if browser is Chrome
 * @returns boolean - true if Chrome
 */
export const isChrome = (): boolean => {
  return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor) && !/Edg/.test(navigator.userAgent);
};

/**
 * Forces a reload of the application, bypassing the cache
 */
export const forceReload = (): void => {
  // Prevent multiple reloads in the same session
  if (refreshAttempted) {
    console.log('Reload already attempted in this session, skipping to prevent refresh loop');
    return;
  }
  
  console.log('Forcing application reload to apply updates...');
  refreshAttempted = true;
  
  // Use standard reload for all browsers
  window.location.reload();
};

/**
 * Clear all application caches
 */
export const clearAllCaches = (): void => {
  try {
    console.log('Clearing application caches...');
    
    // Clear sessionStorage cache items
    let cacheKeysCleared = 0;
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('profiles-')) {
        sessionStorage.removeItem(key);
        cacheKeysCleared++;
      }
    }
    
    console.log(`Application caches cleared (${cacheKeysCleared} items)`);
  } catch (e) {
    console.error('Error clearing caches:', e);
  }
};

// Delegated functions - these are now managed by the version manager
export const verifyAppVersion = (): boolean => {
  // Import dynamically to avoid circular dependencies
  import('./versionManager').then(({ isNewVersion }) => {
    return isNewVersion();
  }).catch(() => {
    console.error('Error importing versionManager');
    return false;
  });
  
  return false;
};

export const handleServiceWorkers = async (): Promise<void> => {
  // Import dynamically to avoid circular dependencies
  import('./versionManager').then(({ unregisterServiceWorkers }) => {
    unregisterServiceWorkers();
  }).catch(() => {
    console.error('Error importing versionManager');
  });
};

export const initCacheControl = (): void => {
  // Import dynamically to avoid circular dependencies
  import('./versionManager').then(({ initVersionManager }) => {
    initVersionManager();
  }).catch(() => {
    console.error('Error importing versionManager');
  });
};
